import { useCallback, useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router'

// How long we keep asking whether the plan has landed. Stripe sends the member
// back the instant the card clears, which can beat the webhook that writes the
// subscription by a second or two. Without this the member would be looking at
// a paid-for plan that is not on screen, with a manual reload as the only cure.
const RETRY_DELAYS_MS = [2000, 4000, 8000]

interface Options {
  /** Whether the plan the member just paid for is on screen yet. */
  hasPlan: boolean
  /** Refetch the member's plan at this gym. */
  refresh: () => Promise<void>
}

/**
 * Coming back from Stripe.
 *
 * `?paid=1` is a one-shot message on the success URL, and `?paid=0` is the
 * cancel URL, which deliberately says nothing: a member who backed out of a
 * payment does not need to be told.
 *
 * Both are stripped from the URL as soon as they are read. The param would
 * otherwise survive a reload, a bookmark and the browser restoring the tab,
 * and "payment received" is not a sentence to repeat to somebody who paid once.
 */
export function usePaidReturn({ hasPlan, refresh }: Options): {
  justPaid: boolean
  dismiss: () => void
} {
  const [params, setParams] = useSearchParams()
  const [justPaid, setJustPaid] = useState(false)

  // Read through a ref: the caller passes a new closure on every render, and
  // this must not restart the retry schedule each time the dashboard rerenders.
  const refreshRef = useRef(refresh)
  useEffect(() => {
    refreshRef.current = refresh
  })

  useEffect(() => {
    const paid = params.get('paid')
    if (paid === null) return

    const next = new URLSearchParams(params)
    next.delete('paid')
    // replace, so Back does not land the member on the same message again.
    setParams(next, { replace: true })

    if (paid === '1') {
      setJustPaid(true)
      void refreshRef.current()
    }
  }, [params, setParams])

  useEffect(() => {
    if (!justPaid || hasPlan) return
    const timers = RETRY_DELAYS_MS.map((delay) =>
      setTimeout(() => void refreshRef.current(), delay),
    )
    // Clears the moment the plan arrives, so a fast webhook costs one request.
    return () => timers.forEach(clearTimeout)
  }, [justPaid, hasPlan])

  const dismiss = useCallback(() => setJustPaid(false), [])

  return { justPaid, dismiss }
}
